import { RUNNER_SYSTEM_PROMPT_MAX_CHARS } from '@developerz/domain';
import { createEffect, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { boxPromptCall } from '../lib/box-lifecycle';
import { type CallOption, CopyAsCall } from './CopyAsCall';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// A box's operator briefing (`PUT /v1/runners/:id/prompt`). Presenter — the
// dialog owns the two writes and hands down the API's refusal.
//
// THE BOX IS NEVER SEEDED WITH THE STORED BRIEFING, and that is the whole design
// of this form. No door returns the prose: `maskBox`, the MCP `box get` verb and
// this write's own echo all project it through `truncateBoxPrompt` — 120 chars
// plus an ellipsis. Seeding the textarea from that preview would make Save cut a
// 2000-char briefing down to its first 120, silently, from a form the reader
// believed showed what was stored. So the preview renders read-only and the
// textarea REPLACES: one behaviour, stated on screen, and identical to what an
// agent can do over MCP.
//
// CLEARING IS ITS OWN ACT, confirmed. `null` wipes prose no surface can read
// back, so it is a deliberate two-step button and never inferred from an empty
// textarea — the prompt-var rule ("an EMPTY box is not `null`") on a field where
// getting it wrong is unrecoverable.

export function BoxPromptForm(props: {
  /** The box being briefed — the address the copied calls carry. */
  readonly boxId: string;
  /** The stored briefing's 120-char preview, or null when the box has none. */
  readonly preview: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSave: (prompt: string) => void;
  readonly onClear: () => void;
}): JSX.Element {
  const [text, setText] = createSignal('');
  const [confirmingClear, setConfirmingClear] = createSignal(false);

  // A landed write empties the box: leaving the prose in it invites a second
  // identical replace, and the preview above is now the answer. Keyed on the
  // pending edge rather than on `preview`, which does not change when the new
  // briefing shares its first 120 chars with the old one.
  createEffect(
    on(
      () => props.pending,
      (now, before) => {
        if (before === true && !now && props.error === null) setText('');
      },
    ),
  );

  const submit = (e: Event): void => {
    e.preventDefault();
    if (text().trim() === '' || props.pending) return;
    props.onSave(text());
  };

  // Exactly the acts the two buttons offer, on the same conditions. A replace
  // is withheld while the textarea is blank because a blank one is not a write
  // this form makes — the API reads `""` as a CLEAR, so a snippet built from an
  // empty box would hand the reader the wipe the form refuses to infer.
  const calls = (): readonly CallOption[] => [
    ...(text().trim() === ''
      ? []
      : [
          {
            label: t('fleet.panel.prompt_save'),
            call: boxPromptCall(props.boxId, text().trim()),
          },
        ]),
    ...(props.preview === null
      ? []
      : [{ label: t('fleet.panel.prompt_clear'), call: boxPromptCall(props.boxId, null) }]),
  ];

  return (
    <form class="field-group box-manage__form" onSubmit={submit}>
      {/* The stored briefing is a read-out, not a control: an <output>, which a
          label can name, so "Current prompt" is its accessible name. */}
      <Field label={t('fleet.panel.prompt_current')} id="box-prompt-current">
        {(a11y) => (
          <output {...a11y} class={props.preview === null ? 'field__desc' : 'box-manage__prompt'}>
            {props.preview ?? t('fleet.panel.prompt_none')}
          </output>
        )}
      </Field>

      <Field
        label={t('fleet.panel.prompt_label')}
        id="box-prompt"
        desc={t('fleet.panel.prompt_desc', { max: RUNNER_SYSTEM_PROMPT_MAX_CHARS })}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="input"
            rows="4"
            maxlength={RUNNER_SYSTEM_PROMPT_MAX_CHARS}
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <div class="box-manage__actions">
        <button
          type="submit"
          class={buttonClass({ size: 'sm' })}
          disabled={text().trim() === '' || props.pending}
        >
          {t(props.pending ? 'fleet.panel.saving' : 'fleet.panel.prompt_save')}
        </button>

        {/* Offered only against a briefing that exists — a clear with nothing to
            clear is a control that can only no-op. */}
        <Show when={props.preview !== null}>
          <Show
            when={confirmingClear()}
            fallback={
              <Button variant="ghost" size="sm" onClick={() => setConfirmingClear(true)}>
                {t('fleet.panel.prompt_clear')}
              </Button>
            }
          >
            <Button variant="outline" size="sm" onClick={() => setConfirmingClear(false)}>
              {t('fleet.panel.cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={props.pending}
              onClick={() => {
                setConfirmingClear(false);
                props.onClear();
              }}
            >
              {t('fleet.panel.prompt_clear_confirm')}
            </Button>
          </Show>
        </Show>
      </div>

      <Show when={calls().length > 0}>
        <CopyAsCall calls={calls()} />
      </Show>
    </form>
  );
}
